import React, { createContext, useContext, useState, useCallback } from 'react';
import { Languages } from 'lucide-react';

// UX-05 — lightweight bilingual helper for GUIDED FLOWS + PRINT SLIPS only
// (not full app-wide i18n — see md/re-work/09-decision-log.md).
//
// Usage:
//   const { t, lang, setLang } = useLang();
//   t({ en: 'Save', te: 'సేవ్ చేయి' })
//   t('gate_pass')   // looks up DICT[key][lang], falls back to English, then the key
//
// Telugu is the first regional language (Andhra shrimp belt); add 'hi' later.

const STORAGE_KEY = 'rework_lang';
export const LANGS = [
  { code: 'en', label: 'English' },
  { code: 'te', label: 'తెలుగు' },
];

// Shared dictionary for common guided-flow labels. Screens may also pass inline
// { en, te } objects to t() without registering a key here.
export const DICT = {
  what_next: { en: 'What to do next', te: 'తర్వాత ఏం చేయాలి' },
  gate_pass: { en: 'Gate Pass', te: 'గేట్ పాస్' },
  save: { en: 'Save', te: 'సేవ్ చేయి' },
  next: { en: 'Next', te: 'తదుపరి' },
  back: { en: 'Back', te: 'వెనుకకు' },
  review: { en: 'Review', te: 'సమీక్ష' },
  scan_to_trace: { en: 'Scan to trace', te: 'ట్రేస్ కోసం స్కాన్ చేయండి' },
  production_tasks: { en: 'Production tasks', te: 'ఉత్పత్తి పనులు' },
  scan_lot: { en: 'Scan or enter traveler / lot', te: 'ట్రావెలర్ లేదా లాట్‌ను స్కాన్ చేయండి' },
  start: { en: 'Start', te: 'ప్రారంభించండి' },
  resume: { en: 'Resume', te: 'మళ్లీ ప్రారంభించండి' },
  complete: { en: 'Complete', te: 'పూర్తి చేయండి' },
  output_weight: { en: 'Output weight', te: 'అవుట్‌పుట్ బరువు' },
  byproduct: { en: 'By-product / waste', te: 'ఉప ఉత్పత్తి / వ్యర్థం' },
  input_weight: { en: 'Input weight', te: 'ఇన్‌పుట్ బరువు' },
  balance: { en: 'Balance', te: 'బ్యాలెన్స్' },
  saved_draft: { en: 'Draft saved on this device', te: 'డ్రాఫ్ట్ ఈ పరికరంలో సేవ్ అయింది' },
  not_saved: { en: 'Not saved to the server yet', te: 'ఇంకా సర్వర్‌లో సేవ్ కాలేదు' },
  qa_release: { en: 'QA release', te: 'QA విడుదల' },
  emit_finished_goods: { en: 'Emit finished goods', te: 'ఫినిష్డ్ గూడ్స్ విడుదల చేయండి' },
  print_traveler: { en: 'Download updated traveler after save', te: 'సేవ్ తర్వాత తాజా ట్రావెలర్‌ను డౌన్‌లోడ్ చేయండి' },
};

const LangContext = createContext(null);

function readInitial() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v && LANGS.some((l) => l.code === v)) return v;
  } catch (_) { /* ignore */ }
  return 'en';
}

export function LangProvider({ children }) {
  const [lang, setLangState] = useState(readInitial);

  const setLang = useCallback((code) => {
    setLangState(code);
    try { localStorage.setItem(STORAGE_KEY, code); } catch (_) { /* ignore */ }
  }, []);

  // translate: accepts an inline {en, te} object or a DICT key string.
  const t = useCallback((keyOrObj) => {
    const entry = typeof keyOrObj === 'string' ? DICT[keyOrObj] : keyOrObj;
    if (!entry) return typeof keyOrObj === 'string' ? keyOrObj : '';
    return entry[lang] || entry.en || (typeof keyOrObj === 'string' ? keyOrObj : '');
  }, [lang]);

  return (
    <LangContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LangContext.Provider>
  );
}

// Works even outside a provider (falls back to a local English-only state),
// so guided-flow components can be dropped in without wiring App.js first.
export function useLang() {
  const ctx = useContext(LangContext);
  const [localLang, setLocalLang] = useState(readInitial);
  if (ctx) return ctx;
  const t = (keyOrObj) => {
    const entry = typeof keyOrObj === 'string' ? DICT[keyOrObj] : keyOrObj;
    if (!entry) return typeof keyOrObj === 'string' ? keyOrObj : '';
    return entry[localLang] || entry.en || (typeof keyOrObj === 'string' ? keyOrObj : '');
  };
  const setLang = (code) => {
    setLocalLang(code);
    try { localStorage.setItem(STORAGE_KEY, code); } catch (_) { /* ignore */ }
  };
  return { lang: localLang, setLang, t };
}

// A compact language toggle for guided-flow / slip headers.
export function LanguageToggle({ className = '' }) {
  const { lang, setLang } = useLang();
  return (
    <div className={`inline-flex items-center gap-1 rounded-md border border-slate-200 p-0.5 ${className}`}>
      <Languages className="ml-1 h-3.5 w-3.5 text-slate-400" />
      {LANGS.map((l) => (
        <button
          key={l.code}
          type="button"
          onClick={() => setLang(l.code)}
          className={`rounded px-2 py-0.5 text-xs font-medium transition-colors ${
            lang === l.code ? 'bg-slate-800 text-white' : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          {l.label}
        </button>
      ))}
    </div>
  );
}
